<template>
    <div class="condition-picker">
        <div v-if="tokens.length" class="d-flex flex-wrap">
            <b-badge
                v-for="(token, idx) in tokens"
                :key="`${idx}-${token}`"
                variant="light"
                class="condition-picker__chip mr-1 mb-1"
                :class="{ 'text-muted': labels[token] === null }"
            >
                <template v-if="isRangeToken(token)">범위 {{ token }}</template>
                <template v-else-if="labels[token]">
                    {{ labels[token].label }}<span v-if="labels[token].sub" class="text-muted"> ({{ labels[token].sub }})</span>
                </template>
                <template v-else>
                    {{ token }}<span v-if="labels[token] === null"> (찾을 수 없음)</span>
                </template>
                <button
                    type="button"
                    class="condition-picker__remove"
                    :disabled="disabled"
                    :aria-label="`${token} 제거`"
                    @click="removeToken(token)"
                >×</button>
            </b-badge>
        </div>
        <div class="position-relative">
            <b-form-input
                v-model="query"
                size="sm"
                autocomplete="off"
                :placeholder="placeholder"
                :disabled="disabled"
                @focus="openMenu"
                @click="openMenu"
                @input="onQueryInput"
                @keydown.esc="open = false"
                @keydown.enter.prevent="addTypedToken"
            />
            <b-list-group v-if="menuVisible" class="condition-picker__menu">
                <b-list-group-item v-if="loading" class="py-1 px-2 small text-muted">검색 중...</b-list-group-item>
                <template v-else>
                    <b-list-group-item
                        v-for="item in results"
                        :key="item.value"
                        button
                        class="py-1 px-2"
                        @click="selectItem(item)"
                    >
                        <div class="small">{{ item.label }}</div>
                        <div v-if="item.sub" class="condition-picker__sub text-muted">{{ item.sub }}</div>
                    </b-list-group-item>
                    <b-list-group-item v-if="!results.length" class="py-1 px-2 small text-muted">결과 없음</b-list-group-item>
                </template>
            </b-list-group>
        </div>
        <small v-if="searchFailed" class="d-block text-danger">검색할 수 없습니다. 값을 직접 입력하고 Enter 로 추가하세요.</small>
        <small v-if="notice" class="d-block text-danger">{{ notice }}</small>
    </div>
</template>

<script>
// 메일 조건값 검색 선택 — 저장 문자열('v' 또는 'a|b')을 칩으로 보여 주고 검색해서 토큰을 추가한다.
// ⛔ CommonJS 헬퍼는 이 코드베이스 관례대로 default import 로 받는다.
import { debounce } from 'lodash'
import conditionTokens from './conditionTokens'

export default {
    name: 'ConditionValuePicker',
    props: {
        // brand | project | campaign | company | user → GET /mail/lookup/<entity>
        entity: { type: String, default: '' },
        // 저장 문자열. 토큰 0개면 ''.
        value: { type: String, default: '' },
        multiple: { type: Boolean, default: true },
        disabled: { type: Boolean, default: false },
        placeholder: { type: String, default: '검색해서 선택' },
    },
    data() {
        return {
            query: '',
            results: [],
            open: false,
            loading: false,
            searchFailed: false,
            notice: '',
            searchSeq: 0,
            // token → { label, sub } | null(찾을 수 없음). 키가 없으면 아직 모름 → 원문 표시.
            labels: {},
        }
    },
    computed: {
        tokens() {
            return conditionTokens.splitTokens(this.value)
        },
        // 기업·사용자는 검색어 없이 목록을 주지 않는다(백엔드도 빈 목록).
        browsable() {
            return ['brand', 'project', 'campaign'].includes(this.entity)
        },
        menuVisible() {
            return this.open && !this.disabled && !this.searchFailed &&
                (this.loading || this.results.length > 0 || this.query.trim() !== '')
        },
    },
    watch: {
        value: {
            immediate: true,
            handler() {
                this.loadLabels()
            },
        },
    },
    created() {
        this.debouncedSearch = debounce(this.search, 300)
    },
    mounted() {
        document.addEventListener('mousedown', this.onDocumentMousedown)
    },
    beforeDestroy() {
        this.debouncedSearch.cancel()
        document.removeEventListener('mousedown', this.onDocumentMousedown)
    },
    methods: {
        isRangeToken: conditionTokens.isRangeToken,
        // 저장된 토큰의 표시 이름을 되찾는다. 실패(권한 등)하면 원문 그대로 둔다.
        async loadLabels() {
            const ids = this.tokens.filter(t => !conditionTokens.isRangeToken(t) && !(t in this.labels))
            if (!this.entity || !ids.length) return
            try {
                const { data } = await this.$axios.get(`/mail/lookup/${this.entity}`, { params: { ids: ids.join(',') } })
                const found = {}
                ;((data && data.items) || []).forEach(item => {
                    found[item.value] = { label: item.label, sub: item.sub }
                })
                const next = { ...this.labels }
                ids.forEach(t => { next[t] = found[t] || null })
                this.labels = next
            } catch (e) { /* 원문 표시 유지 */ }
        },
        openMenu() {
            if (this.disabled || !this.entity) return
            this.open = true
            if (!this.results.length && !this.loading && (this.browsable || this.query.trim())) this.search()
        },
        onQueryInput() {
            this.open = true
            this.notice = ''
            this.debouncedSearch()
        },
        async search() {
            const seq = ++this.searchSeq
            const q = this.query.trim()
            if (!this.entity || (!q && !this.browsable)) {
                this.results = []
                this.loading = false
                return
            }
            this.loading = true
            try {
                const { data } = await this.$axios.get(`/mail/lookup/${this.entity}`, { params: { q } })
                if (seq !== this.searchSeq) return
                this.results = (data && data.items) || []
                this.searchFailed = false
            } catch (e) {
                if (seq !== this.searchSeq) return
                this.results = []
                this.searchFailed = true
            } finally {
                if (seq === this.searchSeq) this.loading = false
            }
        },
        selectItem(item) {
            if (this.addToken(item.value)) {
                this.labels = { ...this.labels, [item.value]: { label: item.label, sub: item.sub } }
            }
        },
        // 검색을 못 쓰는 환경(403 등)에서는 입력값을 그대로 토큰으로 넣는다.
        addTypedToken() {
            const token = this.query.trim()
            if (this.searchFailed && token) this.addToken(token)
        },
        addToken(token) {
            const next = this.multiple ? conditionTokens.joinTokens([...this.tokens, token]) : token
            if (next.length > conditionTokens.MAX_CONDITION_LENGTH) {
                this.notice = `조건 값은 ${conditionTokens.MAX_CONDITION_LENGTH}자를 넘을 수 없어 추가하지 않았습니다.`
                return false
            }
            // 대기·진행 중인 검색 결과가 비운 입력 뒤에 늦게 채워지지 않게 무효화.
            this.debouncedSearch.cancel()
            this.searchSeq++
            this.loading = false
            this.notice = ''
            this.query = ''
            this.results = []
            this.open = false
            this.$emit('input', next)
            return true
        },
        removeToken(token) {
            this.notice = ''
            this.$emit('input', conditionTokens.joinTokens(this.tokens.filter(t => t !== token)))
        },
        onDocumentMousedown(e) {
            if (!this.$el.contains(e.target)) this.open = false
        },
    },
}
</script>

<style scoped>
.condition-picker__chip {
    font-weight: normal;
    text-align: left;
    white-space: normal;
    border: 1px solid #dee2e6;
}
.condition-picker__remove {
    padding: 0 0 0 4px;
    line-height: 1;
    color: inherit;
    background: none;
    border: 0;
}
.condition-picker__menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    min-width: 220px;
    max-height: 240px;
    overflow-y: auto;
}
.condition-picker__sub {
    font-size: 11px;
}
</style>
